'use client';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { Z_INDEX } from '@/constants';
import type { PopBannerCarouselModalProps } from '@/types/pop-banner-carousel-modal.type';
import Autoplay from 'embla-carousel-autoplay';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

export default function PopBannerCarouselModal({
  banners,
  isOpen,
  onClose,
  localStorageKey,
  autoplayDelay = 3000,
  zIndex = Z_INDEX.Modal,
}: PopBannerCarouselModalProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);

  // 최대 3개까지 제한
  const displayBanners = banners.slice(0, 3);

  // 현재 슬라이드 인덱스 추적
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrentSlide(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    onSelect(); // 초기값 설정

    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  // body scroll 막기
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // "오늘 그만보기" 핸들러
  const handleHideToday = () => {
    if (localStorageKey) {
      // const hideUntil = new Date().getTime() + 60 * 1000; // 1분 후
      const hideUntil = new Date().getTime() + 24 * 60 * 60 * 1000; // 24시간 후
      localStorage.setItem(localStorageKey, hideUntil.toString());
    }
    onClose();
  };

  // "닫기" 핸들러
  const handleClose = () => {
    onClose();
  };

  // 배너 클릭 핸들러
  const handleBannerClick = (href?: string) => {
    if (href) {
      // 외부 URL인지 확인
      if (href.startsWith('http://') || href.startsWith('https://')) {
        window.open(href, '_blank', 'noopener,noreferrer');
      } else {
        // 내부 링크는 Next.js 라우팅 사용
        window.location.href = href;
      }
    }
  };

  if (!isOpen || displayBanners.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 top-0 flex items-center justify-center overflow-y-auto"
          style={{ zIndex }}
        >
          {/* Dimm 배경 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/40"
            onClick={handleClose}
          />

          {/* 모바일: 하단 시트 */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="desktop:hidden mobile-w absolute right-0 bottom-0 left-0 mx-auto flex w-full flex-col overflow-hidden rounded-t-2xl bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 배너 캐러셀 */}
            <div className="relative flex flex-1 flex-col overflow-hidden">
              <Carousel
                setApi={setApi}
                opts={{
                  loop: true,
                  align: 'center',
                }}
                plugins={[
                  Autoplay({
                    delay: autoplayDelay,
                    stopOnMouseEnter: true,
                    stopOnInteraction: false,
                  }),
                ]}
              >
                <CarouselContent>
                  {displayBanners.map((banner, index) => (
                    <CarouselItem key={`mobile-${banner.id || index}`}>
                      <div
                        className={`flex justify-center ${
                          banner.href ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => handleBannerClick(banner.href)}
                      >
                        {banner.content}
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              {/* 인디케이터 (점) */}
              {displayBanners.length > 1 && (
                <div className="absolute right-0 bottom-0 left-0 z-10 flex justify-center gap-3 pb-5">
                  {displayBanners.map((_, index) => (
                    <div
                      key={`mobile-indicator-${index}`}
                      className={`h-[10px] w-[10px] rounded-full bg-white transition-opacity ${
                        index === currentSlide ? 'opacity-100' : 'opacity-50'
                      }`}
                      style={{
                        filter: 'drop-shadow(2px 2px 6px rgba(0, 0, 0, 0.25))',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 버튼 영역 */}
            <div className="border-stroke-white flex h-[62px] shrink-0 items-center overflow-hidden rounded-b-2xl border-t">
              {localStorageKey && (
                <>
                  <button
                    onClick={handleHideToday}
                    className="typo-18 text-level-1 flex h-full flex-1 items-center justify-center font-semibold"
                  >
                    오늘 그만보기
                  </button>
                  <div className="bg-stroke-white h-6 w-px" />
                </>
              )}
              <button
                onClick={handleClose}
                className="typo-18 text-level-1 flex h-full flex-1 items-center justify-center font-semibold"
              >
                닫기
              </button>
            </div>
          </motion.div>

          {/* 데스크톱: 중앙 팝업 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="desktop:flex border-stroke-white relative mx-4 hidden h-[553px] w-[460px] flex-col rounded-2xl border bg-white shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 배너 캐러셀 */}
            <div className="relative flex flex-1 flex-col overflow-hidden">
              <Carousel
                setApi={setApi}
                opts={{
                  loop: true,
                  align: 'center',
                }}
                plugins={[
                  Autoplay({
                    delay: autoplayDelay,
                    stopOnMouseEnter: true,
                    stopOnInteraction: false,
                  }),
                ]}
              >
                <CarouselContent>
                  {displayBanners.map((banner, index) => (
                    <CarouselItem key={`desktop-${banner.id || index}`}>
                      <div
                        className={`flex justify-center ${
                          banner.href ? 'cursor-pointer' : ''
                        }`}
                        onClick={() => handleBannerClick(banner.href)}
                      >
                        {banner.content}
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>

              {/* 인디케이터 (점) */}
              {displayBanners.length > 1 && (
                <div className="absolute right-0 bottom-0 left-0 z-10 flex justify-center gap-4 pb-6">
                  {displayBanners.map((_, index) => (
                    <div
                      key={`desktop-indicator-${index}`}
                      className={`h-[10px] w-[10px] rounded-full bg-white transition-opacity ${
                        index === currentSlide ? 'opacity-100' : 'opacity-50'
                      }`}
                      style={{
                        filter: 'drop-shadow(2px 2px 6px rgba(0, 0, 0, 0.25))',
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* 버튼 영역 */}
            <div className="border-stroke-white flex h-[73px] shrink-0 items-center overflow-hidden rounded-b-2xl border-t">
              {localStorageKey && (
                <>
                  <button
                    onClick={handleHideToday}
                    className="typo-22 text-level-1 flex h-full flex-1 cursor-pointer items-center justify-center font-semibold transition-colors hover:bg-gray-50"
                  >
                    오늘 그만보기
                  </button>
                  <div className="bg-stroke-white h-6 w-px" />
                </>
              )}
              <button
                onClick={handleClose}
                className="typo-22 text-level-1 flex h-full flex-1 cursor-pointer items-center justify-center font-semibold transition-colors hover:bg-gray-50"
              >
                닫기
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
